import {
  all,
  AllEffect,
  call,
  CallEffect,
  ForkEffect,
  put,
  race,
  RaceEffect,
  select,
  take,
  TakeEffect,
  takeEvery,
} from 'redux-saga/effects';
import { RemoteRecord } from 'b2b-apps/core/common/redux/RemoteRecord';
import { ADD_TO_WISHLIST_FULFILLED, ADD_TO_WISHLIST_REJECTED } from 'b2b-apps/core/myAccount/wishlists/redux/wishlist/actionTypes';
import {
  FetchWishlistFulfilledAction,
  FetchWishlistRejectedAction,
} from 'b2b-apps/core/myAccount/wishlists/redux/wishlist/types';
import {
  ANALYZE_BARCODE_SCAN,
  AnalyzeBarcodeScanUsageAction,
  createBarcodeAnalyticsEventData,
} from 'b2b-apps/shop/analytics/redux';
import { analyzeEvent } from 'b2b-apps/shop/analytics/redux/tealium/actionCreators';
import { TEALIUM_PROD_ACTION } from 'b2b-apps/shop/analytics/types/types';
import { getProductTileBySku } from 'b2b-apps/shop/product/redux/products/tile/selectors';
import { WishlistType } from 'b2b-common/core/account/api/wishlists';

interface BarcodeAnalyticsEventData {
  barcodeAction: TEALIUM_PROD_ACTION,
  barcode?: string,
  sku?: string | string[],
}

function* analyzeBarcodeEvent(
  {
    barcodeAction,
    barcode,
    sku,
  }: BarcodeAnalyticsEventData,
): Generator<any, any, any> {
  let productTile = null;
  if (sku) {
    productTile = yield select(getProductTileBySku, sku as string);
  }

  yield put(analyzeEvent(
    createBarcodeAnalyticsEventData({
      barcodeAction,
      barcode,
      productTile,
    }),
  ));
}

function* analyzeBarcodeScanUsageSaga(action: AnalyzeBarcodeScanUsageAction): Generator<any, any, any> {
  const {
    barcodeAction,
    barcode,
    sku,
  } = action.payload;

  yield call(analyzeBarcodeEvent, {
    barcodeAction: barcodeAction,
    barcode: barcode,
    sku: sku,
  });
}

function* analyzeScannedProductInteraction(
  action: TEALIUM_PROD_ACTION,
  sku: string,
  barcode?: string,
): Generator<CallEffect, void> {
  yield call(
    analyzeBarcodeEvent,
    {
      barcodeAction: action,
      sku,
      barcode,
    },
  );
}

export function* analyzeScannedProductClick(sku: string, barcode: string): Generator<CallEffect | void> {
  yield call(
    analyzeScannedProductInteraction,
    TEALIUM_PROD_ACTION.productListClick,
    sku,
    barcode,
  );
}

export function* analyzeScannedProductAddToCart(sku: string): Generator<CallEffect, void> {
  yield call(
    analyzeScannedProductInteraction,
    TEALIUM_PROD_ACTION.addToCartFromList,
    sku,
  );
}

export function* analyzeScannedProductAddToWishlist(sku: string):
  Generator<CallEffect | RaceEffect<TakeEffect>,
  void,
  RemoteRecord<WishlistType> & {fulfilled: FetchWishlistFulfilledAction, rejected: FetchWishlistRejectedAction}
  > {
  const { fulfilled, rejected } = yield race({
    fulfilled: take(ADD_TO_WISHLIST_FULFILLED),
    rejected: take(ADD_TO_WISHLIST_REJECTED),
  });

  if (fulfilled) {
    yield call(
      analyzeScannedProductInteraction,
      TEALIUM_PROD_ACTION.addToWishlist,
      sku,
    );
  }

  if (rejected) {
    return;
  }
}

function* barcodeAnalyticsSaga(): Generator<AllEffect<ForkEffect>, void> {
  yield all([
    takeEvery(ANALYZE_BARCODE_SCAN, analyzeBarcodeScanUsageSaga),
  ]);
}

export {
  analyzeBarcodeScanUsageSaga,
  barcodeAnalyticsSaga,
};
